import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Translations come straight from the mobile app's locale files (single source of
// truth) via the `@locales` alias → repo-root /locales.
import en from '@locales/en.json'
import es from '@locales/es.json'
import fr from '@locales/fr.json'
import de from '@locales/de.json'
import pt from '@locales/pt.json'
import ar from '@locales/ar.json'
import it from '@locales/it.json'
import nl from '@locales/nl.json'
import pl from '@locales/pl.json'
import zhTW from '@locales/zh-TW.json'
import ja from '@locales/ja.json'
import ko from '@locales/ko.json'
import tr from '@locales/tr.json'

export const SUPPORTED_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'pt', 'ar', 'it', 'nl', 'pl', 'zh-TW', 'ja', 'ko', 'tr',
] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const RTL_LANGUAGES: readonly Language[] = ['ar'] as const

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  ar: 'العربية',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  'zh-TW': '繁體中文',
  ja: '日本語',
  ko: '한국어',
  tr: 'Türkçe',
}

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
  ar: { translation: ar },
  it: { translation: it },
  nl: { translation: nl },
  pl: { translation: pl },
  'zh-TW': { translation: zhTW },
  ja: { translation: ja },
  ko: { translation: ko },
  tr: { translation: tr },
}

export const isRTLLanguage = (lang: string): boolean =>
  (RTL_LANGUAGES as readonly string[]).includes(lang)

function applyDirection(lang: string) {
  document.documentElement.dir = isRTLLanguage(lang) ? 'rtl' : 'ltr'
  document.documentElement.lang = lang
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    // Several web keys collide with the app's nested namespaces (e.g. progress.insights,
    // settings.weightUnit are OBJECTS in the shared locale files). When a key resolves to
    // an object, fall back to the English default we passed as the 2nd arg instead of
    // rendering the "returned an object" error.
    returnedObjectHandler: (key: string, _value: unknown, options?: { defaultValue?: unknown }) => {
      const dv = options?.defaultValue
      return typeof dv === 'string' ? dv : key.split('.').pop() || key
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'app_language',
      caches: ['localStorage'],
    },
  })
  .then(() => applyDirection(i18n.language))

i18n.on('languageChanged', applyDirection)

export async function changeLanguage(lang: Language): Promise<void> {
  await i18n.changeLanguage(lang)
}

/**
 * The AI callables expect the language NAME (e.g. 'Español'), not the code —
 * mobile sends LANGUAGE_NAMES[getCurrentLanguage()].
 */
export function currentLanguageName(): string {
  const lang = i18n.resolvedLanguage || i18n.language
  return LANGUAGE_NAMES[lang as Language] || LANGUAGE_NAMES.en
}

export default i18n
